import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import InputRange from 'react-input-range';

import { uid } from '../utils/data';
import { getDocumentProp } from '../utils/dom';

import './RangeSelector.css';

export const RangeSelector = ({
	min,
	max,
	value,
	label,
	onChange,
	formatLabel,
	className,
	fps = 10,
	singleValue,
	showYearLabels,
}) => {
	const id = useMemo(() => uid('range'), []);
	const isSingle = typeof value === 'number';
	const throttledOnChange = useCallback(throttle(onChange, 1000 / fps), []);

	const years = useMemo(() => {
		const years = {};
		for (var i = min; i <= max; i++) {
			const parts = formatLabel(i).split('-');
			if (!(parts[0] in years) && parts.length == 2) years[parts[0]] = i;
		}

		return years;
	}, [min, max]);

	const handleChange = (v, t) => {
		// sometimes InputRange does funky things
		if (v.min) v.min = Math.max(v.min, min);
		if (v.max) v.max = Math.min(v.max, max);
		// setVal(v);
		throttledOnChange(v);
	};
	// clickOnTrack
	// only for singleValue
	const ref = useRef();
	useEffect(() => {
		if (!ref.current || !isSingle) return;
		const track = ref.current.querySelector('.input-range__track.input-range__track--background');
		if (!track) return;
		let clicking = false;
		const onTrackMousedown = (e) => {
			clicking = true;
			if (e.target != track) return;
			const percent = e.layerX / e.target.offsetWidth;
			throttledOnChange(Math.round(min + percent * (max - min)));
		};

		const onTrackMousemove = (e) => {
			if (e.target != track || !clicking) return;
			const percent = e.layerX / e.target.offsetWidth;
			throttledOnChange(Math.round(min + percent * (max - min)));
		};

		const onTrackMouseout = (e) => (clicking = false);

		track.addEventListener('mousedown', onTrackMousedown);
		track.addEventListener('mousemove', onTrackMousemove);
		track.addEventListener('mouseup', onTrackMouseout);
		ref.current.addEventListener('mouseleave', onTrackMouseout);
		() => {
			track.removeEventListener('mousedown', onTrackMousedown);
			track.removeEventListener('mousemove', onTrackMousemove);
			track.removeEventListener('mouseup', onTrackMouseout);
			ref.current.removeEventListener('mouseleave', onTrackMouseout);
		};
	}, [ref.current]);

	return (
		<div
			id={id}
			ref={ref}
			className={classnames('input-range__wrapper', className, {
				'input-range--single': isSingle,
				'input-range--near-max': value.max && max - value.max < 3,
				'input-range--near-min': value.min && value.min - min < 3,
			})}>
			<label className={'input-range__outer-label'}>{label}</label>
			<InputRange
				value={value}
				onChange={handleChange}
				minValue={min}
				maxValue={max}
				draggableTrack
				allowSameValues={singleValue}
				formatLabel={formatLabel}
			/>
			<div className={'input-range--year-ticks'}>
				{showYearLabels
					? Object.keys(years).map((y, i) => {
							const pos = ((years[y] - min) * 100) / (max - min);
							return (
								<span
									key={`year-${i}`}
									className={'input-range__label input-range--year-tick'}
									style={{ left: pos + '%' }}>
									{pos > 90 || pos < 10 ? '' : y}
								</span>
							);
					  })
					: null}
			</div>
			<div className={'input-range__print-label'}>
				<strong>{label}: </strong>
				<span>
					{value.min != null ? `${formatLabel(value.min)} to ${formatLabel(value.max)}` : formatLabel(value)}
				</span>
			</div>
		</div>
	);
};

export default React.memo(RangeSelector);
